/**
 * 회의 보고서 마크다운 파서
 * 표준 보고서와 대체 보고서 모두 지원
 */

export class ReportParser {
  constructor() {
    this.footerPatterns = [
      /\*\*본 문서는 MeetSync AI 시스템에 의해 자동 생성되었습니다\.\*\*/g,
      /\*\*This document was automatically generated by the MeetSync AI system\.\*\*/g
    ]
  }

  /**
   * 메인 파싱 함수
   */
  parse(markdownContent) {
    if (!markdownContent?.trim()) {
      return this.getEmptyResult()
    }

    // 보고서 타입 확인
    const isStandardReport = markdownContent.trim().startsWith('# Summary')
    
    if (!isStandardReport) {
      return {
        isStandardReport: false,
        renderedMarkdown: markdownContent
      }
    }

    // 표준 보고서 파싱
    return this.parseStandardReport(markdownContent)
  }

  /**
   * 표준 보고서 파싱
   */
  parseStandardReport(content) {
    // 1. 푸터 분리 및 제거
    const { content: cleanContent, footer } = this.extractFooter(content)
    
    // 2. 볼드 마크다운 제거 (** 제거)
    const normalizedContent = cleanContent.replace(/\*\*/g, '')
    
    // 3. 섹션별 파싱
    const sections = this.splitIntoSections(normalizedContent)
    
    return {
      isStandardReport: true,
      overviewContent: this.parseSummary(sections),
      keywords: this.parseKeywords(sections),
      agendaItems: this.parseAgenda(sections),
      speakerSummaries: this.parseSpeakers(sections),
      actionItems: this.parseActions(sections),
      ...this.parseAIComments(sections),
      hasActionSection: this.hasSection(sections, 'To-do'),
      hasAICommentsSection: this.hasSection(sections, 'AI Comment'),
      footerContent: footer
    }
  }

  /**
   * 푸터 추출 및 제거
   */
  extractFooter(content) {
    let footer = ''
    let cleanContent = content

    for (const pattern of this.footerPatterns) {
      const match = content.match(pattern)
      if (match) {
        footer = match[0].replace(/\*\*/g, '') // ** 제거
        cleanContent = content.replace(pattern, '').trim()
        break
      }
    }

    return { content: cleanContent, footer }
  }

  /**
   * 섹션으로 분할
   */
  splitIntoSections(content) {
    // 연속된 --- 를 하나의 구분자로 처리하여 분할
    const rawSections = content.split(/(?:\n\s*---+\s*)+/)
      .map(section => section.trim())
      .filter(section => section.length > 0)

    // 섹션을 헤더별로 정리
    const sections = {}
    
    rawSections.forEach(section => {
      // 메인 헤더 찾기 (# 또는 ##)
      const headerMatch = section.match(/^(#{1,2})\s+(.+?)(?:\n|$)/m)
      if (headerMatch) {
        const headerLevel = headerMatch[1].length
        const headerText = headerMatch[2].trim()
        
        // 레벨 2 헤더만 섹션으로 처리
        if (headerLevel <= 2) {
          sections[headerText] = section
        }
      }
    })

    return sections
  }

  /**
   * Summary 섹션 파싱
   */
  parseSummary(sections) {
    const summarySection = sections['Summary']
    if (!summarySection) return ''

    // # Summary 다음부터 다음 ## 헤더 또는 --- 까지
    const match = summarySection.match(/^#\s+Summary\s*\n+([\s\S]*?)(?=\n\s*#{2,}|\n\s*---+|$)/m)
    return match ? match[1].trim() : ''
  }

  /**
   * Keywords 섹션 파싱
   */
  parseKeywords(sections) {
    const keywordSection = sections['Keyword']
    if (!keywordSection) return []

    // ## Keyword 다음의 - 로 시작하는 라인들
    const match = keywordSection.match(/^##\s+Keyword\s*\n+([\s\S]*)/m)
    if (!match) return []

    return match[1]
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.startsWith('-'))
      .map(line => line.replace(/^-\s*/, '').trim())
      .filter(keyword => keyword.length > 0)
  }

  /**
   * Agenda 섹션 파싱
   */
  parseAgenda(sections) {
    const agendaSection = sections['Agenda']
    if (!agendaSection) return []

    // ## Agenda 다음 내용 추출
    const match = agendaSection.match(/^##\s+Agenda\s*\n+([\s\S]*)/m)
    if (!match) return []

    const content = match[1]
    const items = []

    // ### 헤더로 분할
    const blocks = content.split(/(?=###\s)/).filter(block => block.trim())

    blocks.forEach(block => {
      const lines = block.trim().split('\n')
      const titleMatch = lines[0]?.match(/^###\s+(.+)/)
      
      if (titleMatch) {
        const title = titleMatch[1].trim()
        const contentLines = []

        // 나머지 라인에서 불릿 포인트 또는 일반 텍스트 수집
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim()
          if (line && !line.startsWith('#')) {
            // 불릿 포인트 제거
            const cleanLine = line.replace(/^[•\-*]\s*/, '').trim()
            if (cleanLine) {
              contentLines.push(cleanLine)
            }
          }
        }

        if (title && contentLines.length > 0) {
          items.push({ title, content: contentLines })
        }
      }
    })

    return items
  }

  /**
   * Participants' Key Remarks 섹션 파싱
   */
  parseSpeakers(sections) {
    const speakersSection = sections["Participants' Key Remarks"]
    if (!speakersSection) return []

    // ## Participants' Key Remarks 다음 내용 추출
    const match = speakersSection.match(/^##\s+Participants'\s+Key\s+Remarks\s*\n+([\s\S]*)/m)
    if (!match) return []

    const content = match[1]
    const speakers = []

    // ### 헤더로 분할
    const blocks = content.split(/(?=###\s)/).filter(block => block.trim())

    blocks.forEach(block => {
      const nameMatch = block.match(/^###\s+([^\n]+)/)
      if (!nameMatch) return

      const name = nameMatch[1].trim()
      
      // Key Remarks: 패턴 찾기 (영어/한국어)
      const remarksMatch = block.match(/(?:Key Remarks|주요 발언):\s*([\s\S]*?)(?=\n\s*#{3,}|$)/i)
      
      if (remarksMatch) {
        const content = remarksMatch[1].trim()
        if (content) {
          speakers.push({ name, content })
        }
      }
    })

    return speakers
  }

  /**
   * To-do 섹션 파싱
   */
  parseActions(sections) {
    const todoSection = sections['To-do']
    if (!todoSection) return []

    // ## To-do 다음 내용 추출
    const match = todoSection.match(/^##\s+To-do\s*\n+([\s\S]*)/m)
    if (!match) return []

    const content = match[1].trim()
    
    // "없음" 또는 "No specific action items" 체크
    if (['없음', 'No specific action items were derived from the meeting.'].some(text => 
        content.includes(text))) {
      return []
    }

    // 불릿 포인트 라인 추출
    const lines = content.split('\n').map(line => line.trim())
    const bulletItems = lines
      .filter(line => /^[•\-*]\s/.test(line))
      .map(line => ({
        text: line.replace(/^[•\-*]\s*/, '').trim(),
        isBullet: true
      }))
      .filter(item => item.text.length > 0)

    // 불릿이 없으면 전체 텍스트를 하나의 항목으로
    if (bulletItems.length === 0 && content.length > 0) {
      return [{ text: content, isBullet: false }]
    }

    return bulletItems
  }

  /**
   * AI Comment 섹션 파싱
   */
  parseAIComments(sections) {
    const aiSection = sections['AI Comment']
    if (!aiSection) {
      return { aiTodoComments: [], aiAgendaComments: [] }
    }

    // ## AI Comment 다음 내용 추출
    const match = aiSection.match(/^##\s+AI\s+Comment\s*\n+([\s\S]*)/m)
    if (!match) {
      return { aiTodoComments: [], aiAgendaComments: [] }
    }

    return this.parseAICommentsContent(match[1])
  }

  /**
   * AI Comment 내용 파싱
   */
  parseAICommentsContent(content) {
    const todos = []
    const agendas = []

    // ### 헤더로 AI 코멘트 항목들을 분할
    const blocks = content.split(/(?=###\s)/).filter(block => block.trim())

    blocks.forEach(block => {
      // ### To-do: 또는 ### Agenda: 패턴 찾기
      const headerMatch = block.match(/^###\s+(To-do|Agenda):\s*(.+?)(?:\n|$)/m)
      if (!headerMatch) return

      const type = headerMatch[1]
      const title = headerMatch[2].trim()

      // 나머지 내용에서 Analysis, References, Recommendation 추출
      const restContent = block.substring(headerMatch.index + headerMatch[0].length)
      
      const analysis = this.extractField(restContent, 'Analysis', '분석')
      const references = this.extractReferences(restContent)
      const recommendation = this.extractField(restContent, 'Recommendation', '실무 권고사항', 'suggestion')

      if (analysis) {
        const commentItem = { title, analysis, references, recommendation }
        
        if (type === 'To-do') {
          todos.push(commentItem)
        } else if (type === 'Agenda') {
          agendas.push(commentItem)
        }
      }
    })

    return { aiTodoComments: todos, aiAgendaComments: agendas }
  }

  /**
   * AI Comment에서 필드 추출 (Analysis, Recommendation 등)
   */
  extractField(content, ...fieldNames) {
    for (const fieldName of fieldNames) {
      const pattern = new RegExp(`${fieldName}:\\s*([\\s\\S]*?)(?=(?:Analysis|References|Recommendation|suggestion):|$)`, 'i')
      const match = content.match(pattern)
      if (match) {
        return match[1].trim()
      }
    }
    return ''
  }

  /**
   * AI Comment에서 참고 자료 URL 추출
   */
  extractReferences(content) {
    const referencesMatch = content.match(/References:\s*([\s\S]*?)(?=(?:Analysis|Recommendation|suggestion):|$)/i)
    if (!referencesMatch) return []

    const urls = referencesMatch[1].match(/https?:\/\/[^\s\n]+/g) || []
    return urls.map(url => url.trim())
  }

  /**
   * 섹션 존재 여부 확인
   */
  hasSection(sections, sectionName) {
    return Object.prototype.hasOwnProperty.call(sections, sectionName)
  }

  /**
   * 빈 결과 반환
   */
  getEmptyResult() {
    return {
      isStandardReport: true,
      overviewContent: '',
      keywords: [],
      agendaItems: [],
      speakerSummaries: [],
      actionItems: [],
      aiTodoComments: [],
      aiAgendaComments: [],
      hasActionSection: false,
      hasAICommentsSection: false,
      footerContent: ''
    }
  }
}

// 호스트네임 추출 유틸리티 함수
export function getHostname(url) {
  try {
    return new URL(url).hostname
  } catch (e) {
    const match = url.match(/^(?:https?:\/\/)?(?:[^@\n]+@)?(?:www\.)?([^:/\n?]+)/im)
    return match ? match[1] : url
  }
}
